import lowerCase from "lodash.lowercase";

import type {
  AudienceAction,
  ExpressionNodeQueryType,
  ExpressionNodeType,
} from "@intelligems/ig-types/dtos/audiences";

import { postfixToInfix } from "./expression";

export function renderQueryText(query: ExpressionNodeQueryType): string {
  if (query.key === "screenLayout" || query.key === "device") {
    return `Device type is ${query.value}`;
  }
  if (query.key === "visitor") {
    return `Visitor is ${query.value}`;
  }
  if (query.key === "jsExpression") {
    return `(${query.value}) evaluates to true`;
  }

  return `${query.key} ${lowerCase(query.filter || "")} ${
    query.filter !== "isNotNull" && query.filter !== "isNull" ? query.value : ""
  }`;
}

/** Renders a postfix boolean expression tree as a readable "and"/"or"/parens sentence. */
export function renderConditionText(
  expression: ExpressionNodeType[] | null | undefined
): string {
  if (!expression || expression.length === 0) {
    return "";
  }

  const infix = postfixToInfix(expression);
  let result = "";
  for (const node of infix) {
    if (node.operator) {
      if (node.operator === "start_paren") {
        result += "(";
      } else if (node.operator === "end_paren") {
        result += ")";
      } else {
        result += ` ${node.operator} `;
      }
    } else if (node.query) {
      result += renderQueryText(node.query);
    }
  }
  return result;
}

export type AudienceActionEntity = "experiment" | "campaign";

export function renderAudienceActionText(
  action: AudienceAction | null | undefined,
  {
    variationId,
    excludeFromAnalytics,
    variations,
    entity,
  }: {
    variationId?: string | null;
    excludeFromAnalytics?: boolean;
    variations: { id: string; name: string }[];
    entity: AudienceActionEntity;
  }
): string {
  switch (action) {
    case "experienceInclude":
      return `include in ${entity}`;
    case "randomVariation":
      return "assign to random group";
    case "assignVariation": {
      const assignedGroup = variations.find(
        (variation) => variation.id === variationId
      );
      const suffix = excludeFromAnalytics ? " and exclude from analytics" : "";
      return assignedGroup
        ? `assign to ${assignedGroup.name}${suffix}`
        : `assign to specific group${suffix}`;
    }
    case "experienceExclude":
      return `exclude from ${entity}`;
    case "experienceUnassigned":
    case "leaveUnassigned":
      return "leave unassigned";
    default:
      return "assign to random group";
  }
}
